import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { App, type InputRef } from 'antd';
import { useTranslation } from 'translations/hooks';
import { getLocalStorageValue } from 'technical/local-storage';
import { SCAN_PATTERN_KEY } from 'technical/local-storage/constants';
import {
  scanPatternSchema,
  type ScanPatternSchema,
} from 'technical/local-storage/types';
import { db, type Ring } from 'technical/db';
import dayjs from 'dayjs';
import { SegmentStatus } from 'business/receipt/constants';
import { AlertAction } from 'ui/components/alert-drawer';
import { createSearchParams, useNavigate } from 'react-router';
import { Routes } from 'business/router/constants';
import { isVirtualRingId } from 'business/tunnel/pages/ring-scan/constants';
import { useNbSegmentsByRing } from 'business/tunnel/hooks/use-segments-per-ring';

export const useTunnelScanner = (ring: Ring | null) => {
  const ringId = ring?.id ?? null;
  const { t } = useTranslation();
  const { message } = App.useApp();

  const inputRef = useRef<InputRef>(null);
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [newSegmentDrawerOpen, setNewSegmentDrawerOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertDrawerOpen, setAlertDrawerOpen] = useState(false);
  const [alertAction, setAlertAction] = useState(AlertAction.Cancel);
  const [pendingSerialNumber, setPendingSerialNumber] = useState<string | null>(
    null,
  );
  const [isUnknownSegment, setIsUnknownSegment] = useState(false);
  const [ringComplete, setRingComplete] = useState(false);

  const scanPattern = getLocalStorageValue<ScanPatternSchema>({
    key: SCAN_PATTERN_KEY,
    schema: scanPatternSchema,
  });

  useEffect(() => {
    const completeTimer = setTimeout(() => {
      if (ringComplete) {
        setRingComplete(false);
        navigate(
          `${Routes.TunnelRingComplete}?${createSearchParams({ ringNumber: ring?.number.toString() ?? '' })}`,
        );
      }
    }, 500);

    return () => {
      clearTimeout(completeTimer);
    };
  }, [navigate, ring?.number, ringComplete]);

  const resetAndFocus = useCallback(() => {
    setSearchValue('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  const { maxSegmentsByRing, currentRingInstalledSegments } =
    useNbSegmentsByRing(ring);

  const processSerialNumber = useCallback(
    async (serialNumber: string) => {
      if (!ringId) {
        return;
      }

      if (
        maxSegmentsByRing &&
        currentRingInstalledSegments &&
        currentRingInstalledSegments >= maxSegmentsByRing
      ) {
        setAlertMessage(t('tunnel.scanner.ringComplete'));
        setAlertDrawerOpen(true);
        setAlertAction(AlertAction.Cancel);
        return;
      }

      setIsUpdating(true);
      try {
        const segment = await db.segments
          .where('serialNumber')
          .equals(serialNumber)
          .first();

        if (!segment) {
          // it is a new segment not imported
          setIsUnknownSegment(true);
          setPendingSerialNumber(serialNumber);
          setNewSegmentDrawerOpen(true);
          return;
        }

        if (segment.ringId) {
          setAlertMessage(t('tunnel.scanner.segmentAlreadyInRing'));
          setAlertDrawerOpen(true);
          setAlertAction(AlertAction.Cancel);
          return;
        }

        if (!segment.segmentType) {
          // the segment type is unknown
          setIsUnknownSegment(false);
          setPendingSerialNumber(serialNumber);
          setNewSegmentDrawerOpen(true);
          return;
        }

        const segmentWithSameType = await db.segments
          .where('ringId')
          .equals(ringId)
          .filter(
            (s) =>
              s.segmentType === segment.segmentType &&
              s.serialNumber !== segment.serialNumber,
          )
          .first();

        if (segmentWithSameType) {
          setAlertMessage(t('tunnel.scanner.duplicateSegmentType'));
          setAlertDrawerOpen(true);
          setAlertAction(AlertAction.Cancel);
          return;
        }

        if (segment.status === SegmentStatus.Quarantine) {
          setAlertMessage(t('tunnel.scanner.segmentInQuarantine'));
          setAlertDrawerOpen(true);
          setAlertAction(AlertAction.Validate);
        }

        if (
          segment.status === SegmentStatus.Rejected ||
          segment.status === SegmentStatus.TunnelRejected
        ) {
          setAlertMessage(t('tunnel.scanner.segmentRejected'));
          setAlertDrawerOpen(true);
          setAlertAction(AlertAction.Validate);
        }

        const isVirtual = ringId != null && isVirtualRingId(ringId);
        await db.segments.update(segment.serialNumber, {
          segmentType: segment.segmentType,
          ringId,
          status: isVirtual ? SegmentStatus.Virtual : SegmentStatus.Installed,
          virtualRingNumber: isVirtual ? ring?.number : undefined,
          desynchronizedAt: dayjs().toISOString(),
        });
      } finally {
        setIsUpdating(false);
      }

      if (
        maxSegmentsByRing &&
        currentRingInstalledSegments &&
        currentRingInstalledSegments + 1 >= maxSegmentsByRing
      ) {
        setRingComplete(true);
      }
    },
    [currentRingInstalledSegments, maxSegmentsByRing, ring?.number, ringId, t],
  );

  const confirmNewSegment = useCallback(
    async (segmentType: string) => {
      if (!ringId || !pendingSerialNumber) {
        setNewSegmentDrawerOpen(false);
        return;
      }

      const duplicate = await db.segments
        .where('ringId')
        .equals(ringId)
        .filter(
          (s) =>
            s.segmentType === segmentType &&
            s.serialNumber !== pendingSerialNumber,
        )
        .first();

      if (duplicate) {
        setAlertMessage(t('tunnel.scanner.duplicateSegmentType'));
        setAlertDrawerOpen(true);
        setAlertAction(AlertAction.Cancel);
        setNewSegmentDrawerOpen(false);
        return;
      }

      const isVirtual = ringId != null && isVirtualRingId(ringId);
      const resolvedStatus = isVirtual
        ? SegmentStatus.Virtual
        : SegmentStatus.Installed;
      const virtualRingNumber = isVirtual ? ring?.number : undefined;

      if (!isUnknownSegment) {
        await db.segments.update(pendingSerialNumber, {
          segmentType,
          ringId,
          status: resolvedStatus,
          virtualRingNumber,
          desynchronizedAt: new Date().toISOString(),
        });
      } else {
        await db.segments.add({
          serialNumber: pendingSerialNumber,
          segmentType,
          ringId,
          status: resolvedStatus,
          virtualRingNumber,
          desynchronizedAt: dayjs().toISOString(),
        });
      }

      message.success(t('tunnel.scanner.success'));

      setSearchValue('');
      setNewSegmentDrawerOpen(false);
      setPendingSerialNumber(null);

      if (
        maxSegmentsByRing &&
        currentRingInstalledSegments &&
        currentRingInstalledSegments + 1 >= maxSegmentsByRing
      ) {
        setRingComplete(true);
      }
    },
    [
      ringId,
      pendingSerialNumber,
      ring?.number,
      isUnknownSegment,
      message,
      t,
      maxSegmentsByRing,
      currentRingInstalledSegments,
    ],
  );

  const cancelNewSegment = useCallback(() => {
    setSearchValue('');
    setNewSegmentDrawerOpen(false);
    setPendingSerialNumber(null);
  }, []);

  const submitSearch = useCallback(
    (rawSerialNumber: string) => {
      if (
        !rawSerialNumber ||
        !scanPattern ||
        rawSerialNumber.length < scanPattern.extractLength
      ) {
        return;
      }

      const serialNumber =
        rawSerialNumber.length === scanPattern.extractLength
          ? rawSerialNumber
          : rawSerialNumber.slice(
              scanPattern.startIndex,
              scanPattern.startIndex + scanPattern.extractLength,
            );

      setSearchValue('');
      processSerialNumber(serialNumber);
    },
    [scanPattern, processSerialNumber],
  );

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    if (scanPattern && e.target.value.length >= scanPattern.extractLength) {
      submitSearch(e.target.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const value = inputRef.current?.input?.value;
      if (value) {
        submitSearch(value);
      }
    }
  };

  return {
    inputRef,
    searchValue,
    handleInputChange,
    handleKeyDown,
    isUpdating,
    newSegmentDrawerOpen,
    pendingSerialNumber,
    confirmNewSegment,
    cancelNewSegment,
    alertMessage,
    alertDrawerOpen,
    setAlertDrawerOpen,
    alertAction,
    resetAndFocus,
  };
};
